import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { unwrapConnection } from "@utils/utils";
import { FC, useState } from "react";

import { Button, Headline, Small, TabNavigation } from "@fronterahealth/frontera-ui-components";

import {
  useCreateOrganizationSuggestionCriteriaMutation,
  useGetUserOrgnizationSuggestionConfigQuery,
  useUpdateOrganizationSuggestionCriteriaMutation,
  useUpsertOrganizationSuggestionCriteriaMutation,
} from "@api/graphql/types-and-hooks";
import { AddCriteriaForm } from "@pages/QASettings/AddCriteriaForm";
import { CriteriaCard } from "@pages/QASettings/CriteriaCard";

type Tab = "Base" | "Custom";
const TABS: Tab[] = ["Base", "Custom"];

export const OrgQASettings: FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>("Base");
  const [showAddForm, setShowAddForm] = useState(false);

  const { data, refetch, isLoading, isFetching } = useGetUserOrgnizationSuggestionConfigQuery({});

  const { mutateAsync: createCriteria, isPending: isCreating } = useCreateOrganizationSuggestionCriteriaMutation({});

  const { mutateAsync: updateCriteria } = useUpdateOrganizationSuggestionCriteriaMutation({});

  const { mutateAsync: upsertCriteria } = useUpsertOrganizationSuggestionCriteriaMutation({});

  const allCriteria = data?.getUserOrgnizationDetails?.suggestionCriteria ?? [];
  const baseCriteria = allCriteria.filter((c) => c?.suggestionCriteria.isBaseCriteria);
  const customCriteria = allCriteria.filter((c) => c && !c.suggestionCriteria.isBaseCriteria);

  const handleCreate = async (name: string, category: string, content: string) => {
    await createCriteria({ name, content, category });
    setShowAddForm(false);
    refetch();
  };

  const handleToggleEnabled = async (id: string, enabled: boolean) => {
    await updateCriteria({ id, enabled });
    refetch();
  };

  const handleToggleEnabledVirtual = async (suggestionCriteriaId: string, enabled: boolean) => {
    await upsertCriteria({ suggestionCriteriaId, enabled });
    refetch();
  };

  const handleContentSave = async (id: string, content: string) => {
    await updateCriteria({ id, content });
    refetch();
  };

  const handleCategorySave = async (id: string, category: string) => {
    await updateCriteria({ id, category });
    refetch();
  };

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center rounded-lg border border-dashed border-gray-300 p-12">
        <Small colorType="secondary">Loading...</Small>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full flex-col content-between items-center lg:flex-col lg:items-start">
      <div className="mt-10 flex w-full items-center justify-between">
        <Headline size="medium">Organization QA Criteria</Headline>
        {activeTab === "Custom" && (
          <Button
            appearance="primary"
            onClick={() => setShowAddForm((prev) => !prev)}
            text={showAddForm ? "Cancel" : "Add Criteria"}
          />
        )}
      </div>

      <div
        className="mt-6 w-full"
        onClick={(e) => {
          const target = e.target as HTMLElement;
          if (target.tagName === "A") {
            const tabName = target.textContent as Tab;
            if (TABS.includes(tabName)) {
              setActiveTab(tabName);
              setShowAddForm(false);
            }
          }
        }}
      >
        <TabNavigation
          edgeToEdge
          tabs={TABS.map((tab) => ({
            type: "simple" as const,
            name: tab,
            href: "#",
            current: activeTab === tab,
          }))}
        />
      </div>

      {activeTab === "Custom" && showAddForm && (
        <AddCriteriaForm isSubmitting={isCreating} onSubmit={handleCreate} onCancel={() => setShowAddForm(false)} />
      )}

      {isFetching && !isLoading && (
        <div className="mt-4 flex items-center gap-2 text-sm text-gray-400">
          <ArrowPathIcon className="h-4 w-4 animate-spin" />
          <Small colorType="secondary">Refreshing...</Small>
        </div>
      )}

      <div className="mt-6 flex w-full flex-col gap-4">
        {activeTab === "Base" &&
          (baseCriteria.length > 0 ? (
            baseCriteria.map((orgCriteria) => {
              if (!orgCriteria) return null;
              return (
                <div
                  key={orgCriteria.id ?? orgCriteria.suggestionCriteria.id}
                  className="w-full rounded-lg border border-gray-200 bg-white p-4 shadow-xs"
                >
                  <div className="flex items-center justify-between">
                    <Small>{orgCriteria.suggestionCriteria.name}</Small>
                    <label className="flex cursor-pointer items-center gap-2">
                      <Small>Enabled</Small>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded-sm border-gray-300 text-hatch-600 focus:ring-hatch-600"
                        checked={orgCriteria.enabled}
                        onChange={(e) => {
                          if (orgCriteria.isVirtual) {
                            handleToggleEnabledVirtual(orgCriteria.suggestionCriteria.id, e.target.checked);
                          } else {
                            handleToggleEnabled(orgCriteria.id, e.target.checked);
                          }
                        }}
                      />
                    </label>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="flex w-full items-center justify-center rounded-lg border border-dashed border-gray-300 p-12">
              <Small colorType="secondary">No base criteria configured.</Small>
            </div>
          ))}

        {activeTab === "Custom" &&
          (customCriteria.length > 0 ? (
            customCriteria.map((orgCriteria) => {
              if (!orgCriteria) return null;

              let otherOrgNames: string[] = [];
              const connections = unwrapConnection(orgCriteria.suggestionCriteria.organizations);
              if (connections && connections.length > 1) {
                otherOrgNames = connections.map(({ organization }) => organization.name);
              }

              return (
                <CriteriaCard
                  key={orgCriteria.id}
                  criteria={orgCriteria}
                  relatedNames={otherOrgNames}
                  onToggleEnabled={handleToggleEnabled}
                  onContentSave={handleContentSave}
                  onCategorySave={handleCategorySave}
                />
              );
            })
          ) : (
            <div className="flex w-full items-center justify-center rounded-lg border border-dashed border-gray-300 p-12">
              <Small colorType="secondary">No custom criteria yet. Use "Add Criteria" to create one.</Small>
            </div>
          ))}
      </div>
    </div>
  );
};
